import {
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
} from '@chakra-ui/react';
import { NodeData } from '../flowEditorBlockNodeTypes';
import { LucideMoreHorizontal, LucideTrash } from 'lucide-react';
import { colors } from '@duke-hq/ui/theme/chakraTheme';
import { DukeQuestionOptionsEditor } from '../../../tiptap/DukeQuestion';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { JSONContent } from '@tiptap/core';
import { Node } from 'reactflow';
import { useDebouncedCallback } from 'use-debounce';
import { QuizOutgoingMenu } from './QuizOutgoingMenu';
import { flowEditorState } from '../../flowEditorState';

export function SelectQuiz({
  nodeId,
  isConnectable,
  setNodeData,
  node,
}: {
  nodeId: string;
  isConnectable: boolean;
  node: Node;
  // Applies the new node data, marks the flow dirty (so it autosaves) and takes
  // an undo snapshot. Always passed a fully-built NodeData, never a functional
  // updater.
  setNodeData: (data: NodeData) => void;
}) {
  const nodeData = node.data as NodeData;

  const updateQuestion = trpcAdmin.questions.update.useMutation();

  const questionQuery = trpcAdmin.questions.byId.useQuery(nodeData.quizId!, {
    enabled: !!nodeData.quizId,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const debouncedUpdateQuestionHandler = useDebouncedCallback(async (e) => {
    await updateQuestion.mutateAsync({
      id: nodeData.quizId!,
      newContent: [
        {
          type: 'text',
          text: e.target.value,
        },
      ],
    });
  }, 600);

  if (nodeData.quizId) {
    // node had a question reference
    const content = questionQuery.data?.content as JSONContent;

    const editor = flowEditorState.nodeIdToEditorMap.get(node.id);
    if (!editor) {
      // Only the selected node mounts an editor; a quiz on an unselected node
      // has no live editor to drive, so render nothing here.
      return null;
    }

    return (
      <Flex gap={4}>
        <Flex
          flexDir="column"
          gap="8px"
          borderRadius="8px"
          bgColor={colors.quizBg}
          p="8px"
          alignItems="flex-end"
          justifyContent="center"
          zIndex={51}
        >
          <InputGroup>
            <Input
              variant="outline"
              size="sm"
              _placeholder={{ color: 'black' }}
              defaultValue={content?.[0]?.text} // TODO we should render whole tiptap editor here
              onChange={debouncedUpdateQuestionHandler}
            />
            <InputRightElement>
              <Menu>
                <MenuButton
                  opacity={0}
                  _groupHover={{
                    opacity: 1,
                  }}
                >
                  <LucideMoreHorizontal size="20px" color="gray" />
                </MenuButton>
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      setNodeData({
                        ...nodeData,
                        quizId: undefined,
                        options: undefined,
                      });
                    }}
                    gap={2}
                    icon={<LucideTrash size="16px" />}
                  >
                    Delete
                  </MenuItem>
                </MenuList>
              </Menu>
            </InputRightElement>
          </InputGroup>

          <Flex flexDirection="column" width={'306px'}>
            <DukeQuestionOptionsEditor questionId={nodeData.quizId} editor={editor} />
          </Flex>
        </Flex>
        <VStack alignItems="start" justifyContent="center" gap={1}>
          {nodeData.options?.map((option) => {
            return (
              <QuizOutgoingMenu
                key={option.id}
                nodeId={nodeId}
                option={option}
                isConnectable={isConnectable}
              />
            );
          })}
        </VStack>
      </Flex>
    );
  }

  return null;
}
